import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProductNameService } from 'src/app/services';
import {
  AddReleaseItemsRequest, AddReleaseItemsResponse,
  ListReleasesRequest, ListReleasesResponse, ListStyleTemplatesRequest, ListStyleTemplatesResponse, ProductName, Release, 
  SituViewWebApiClient, StyleConfigTemplate
} from 'situview-webapi-client';
import { FormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectableItem } from 'src/app/shared/models/selectable-item';

@Component({
  selector: 'release-style-template-dialog',
  templateUrl: './release-style-template-dialog.component.html',
  styleUrls: ['./release-style-template-dialog.component.less']
})
export class ReleaseStyleTemplateDialogComponent {
  productNames = ProductName;
  selectedProductName: ProductName | undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReleaseStyleTemplateDialogComponentData,
    private dialogRef: MatDialogRef<ReleaseStyleTemplateDialogComponent>,
    private snackBar: MatSnackBar,
    private situViewApiClient: SituViewWebApiClient,
    private productNameService: ProductNameService) {
    this.selectedProductName = this.data.productName;
    this.loadStyleTemplates();
    this.loadReleases();
  }

  selectProductName(productName: ProductName) {
    this.selectedProductName = productName;
  }
  
  selectTemplate(template: SelectableItem) {
    let selectableItem = this.filteredStyleConfigTemplates?.find(x => x.item === template.item);
    if (selectableItem) {
      selectableItem.selected = !selectableItem.selected;
    }
  }

  selectRelease(releaseId: string) {
    const index = this.selectedReleases.findIndex(x => x === releaseId);
    if (index === -1) {
      this.selectedReleases.push(releaseId);
    } else {
      this.selectedReleases.splice(index, 1);
    }
  }

  searchTemplatesField = new FormControl();
  styleConfigTemplates: SelectableItem[] | undefined;
  filteredStyleConfigTemplates: SelectableItem[] | undefined;
  doneSelectingTemplates: boolean = false;
  getSelectedItemCount(): number {
    return this.filteredStyleConfigTemplates?.filter(item => item.selected)?.length ?? 0;
  }
  loadStyleTemplates() {
    //this.loading = true;

    let request = new ListStyleTemplatesRequest();
    request.companyId = this.data.companyId;
    request.isHidden = false;
    request.showAll = true;
    if (this.selectedProductName !== undefined) request.productName = this.selectedProductName;

    this.situViewApiClient.listStyleTemplates(request)
      .then((response: ListStyleTemplatesResponse) => {
        if (response.success && response.styleTemplates) {
          this.styleConfigTemplates = [...new Set(
            response.styleTemplates
              .map((x: StyleConfigTemplate) => x.name ?? "")
              .filter((x: string) => x)
          )].map(x => new SelectableItem(x, false));
          this.filteredStyleConfigTemplates = [...this.styleConfigTemplates];
        } else {
          this.showMessage("There was an issue retrieving style templates!");
        }
        //this.loading = false;
      })
      .catch((err: any) => {
        //this.loading = false;
        this.showMessage("Could not retrieve style templates!");
      });
  }
  doTemplateFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredStyleConfigTemplates = this.styleConfigTemplates?.filter(e => { return e.item?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredStyleConfigTemplates = this.styleConfigTemplates;
    }
  }

  searchReleasesField = new FormControl();
  releases: Release[] | undefined;
  filteredReleases: Release[] | undefined;
  selectedReleases: string[] = [];
  doneSelectingReleases: boolean = false;
  isReleaseSelected(releaseId: string): boolean {
    return this.selectedReleases.includes(releaseId);
  }
  loadReleases() {
    //this.loading = true;

    let request = new ListReleasesRequest();
    request.companyId = this.data.companyId;
    request.isNotReleased = true;

    this.situViewApiClient.listReleases(request)
      .then((response: ListReleasesResponse) => {
        if (response.success && response.items) {
          this.releases = response.items;
          this.filteredReleases = [...this.releases];
        } else {
          this.showMessage("There was an issue retrieving releases!");
        }
        //this.loading = false;
      })
      .catch((err: any) => {
        //this.loading = false;
        this.showMessage("Could not retrieve releases!");
      });
  }
  doReleaseFilter = (value: string) => {
    if (value) {
      let searchTerm = value.trim().toLocaleLowerCase();
      this.filteredReleases = this.releases?.filter(e => { return e.name?.toLowerCase().includes(searchTerm)});
    } else {
      this.filteredReleases = this.releases;
    }
  }

  /* releases */
  addingReleaseItems: boolean = false;
  async addReleaseItems() {
    if (this.addingReleaseItems) {
      return;
    }

    let request = new AddReleaseItemsRequest();
    request.companyId = this.data.companyId;
    request.releaseIds = [...this.selectedReleases];
    request.homeConfigIds = [...this.data.homeConfigIds];
    request.styleConfigTemplates = this.filteredStyleConfigTemplates?.filter(x => x.selected).map(x => x.item) ?? [];
    request.productName = this.selectedProductName;
    request.accessoryBundleId = this.data.accessoryBundleId;

    this.addingReleaseItems = true;
    try {
      const response: AddReleaseItemsResponse = await this.situViewApiClient.addReleaseItems(request);
      if (!response?.success) {
        this.showMessage(response?.errorMessages?.join(' ') || "Could not add release items!");
        return;
      }

      this.dialogRef.close({
        releaseItemCount: response.totalAdded,
        releaseCount: this.selectedReleases.length,
        skippedCount: response.totalSkipped,
        warnings: response.warnings
      } as ReleaseStyleTemplateDialogComponentResultData);
    } catch (err) {
      this.showMessage("Could not add release items!");
    } finally {
      this.addingReleaseItems = false;
    }
  }
  /* releases */

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName)?.productColor;
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

export interface ReleaseStyleTemplateDialogComponentData {
  companyId: string;
  productName: ProductName;
  homeConfigIds: string[];
  accessoryBundleId?: string;
}

export interface ReleaseStyleTemplateDialogComponentResultData {
  releaseItemCount: number | undefined;
  releaseCount: number | undefined;
  skippedCount?: number;
  warnings?: string[];
}
